import React, { useState } from 'react';
import { Platform, Pressable, ScrollView, TextInput, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { getCatalogPalette } from '@/components/Catalog/catalogPalette';

export const TOOLS_HERO_PAD_X = { xs: 15, sm: 20, lg: 20 };

function ToolsHero({ headline, subtitle, countLabel, search, categories = [], activeCategorySlug = null, onCategoryChange, allLabel = 'All tools' }) {
  const { colors, theme } = useTheme();
  const { isXS } = useResponsiveness();
  const palette = getCatalogPalette(theme);
  const [focused, setFocused] = useState(false);
  const padX = isXS ? TOOLS_HERO_PAD_X.xs : TOOLS_HERO_PAD_X.lg;
  const hasValue = !!search?.value;
  const tiles = [{ slug: null, name: allLabel, icon: 'grid-2' }, ...categories.filter(cat => cat?.slug)].map(cat => {
    const active = activeCategorySlug === cat.slug;
    return (
      <Pressable key={cat.slug || '__all'} onPress={() => onCategoryChange?.(cat.slug)}
        accessibilityRole="button" accessibilityLabel={cat.name || cat.slug} accessibilityState={{ selected: active }}
        style={({ hovered, pressed }) => ({ flexDirection: 'row', alignItems: 'center', gap: 7, minHeight: 36, paddingHorizontal: 12,
          borderRadius: 9, borderWidth: 1, borderColor: active ? palette.activeBorder : palette.border,
          backgroundColor: hovered || pressed ? palette.hover : active ? palette.selected : palette.surface })}>
        <Icon name={cat.icon || 'grid-2'} size={13} color={active ? colors.text_color_primary : palette.icon} />
        <Text size="s" weight={active ? 'semibold' : 'medium'} color={active ? 'primary' : 'secondary'} numberOfLines={1}>{cat.name || cat.slug}</Text>
      </Pressable>
    );
  });

  return (
    <View style={{ width: '100%', paddingHorizontal: padX, paddingTop: isXS ? 20 : 28, paddingBottom: 8 }}>
      <Text tag="h1" weight="bold" style={{ fontSize: 32, lineHeight: 38, letterSpacing: -0.8 }}>{headline}</Text>
      {!!subtitle && <Text tag="p" color="secondary" style={{ marginTop: 8, fontSize: 14, lineHeight: 21, maxWidth: 640 }}>{subtitle}</Text>}
      {search && typeof search.onChangeText === 'function' && (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 18, height: 44, paddingHorizontal: 12,
          borderRadius: 10, borderWidth: 1, borderColor: focused ? palette.activeBorder : palette.border, backgroundColor: palette.surface }}>
          <Icon name="magnifying-glass" size={16} color={palette.icon} />
          <TextInput value={search.value ?? ''} onChangeText={search.onChangeText}
            onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
            placeholder={search.placeholder} accessibilityLabel={search.placeholder} placeholderTextColor={colors.text_color_tertiary}
            style={{ flex: 1, minWidth: 0, paddingVertical: 0, fontSize: 14, color: colors.text_color_primary, ...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {}) }}
            autoCorrect={false} autoCapitalize="none" returnKeyType="search" testID={search.testID} />
          {hasValue ? (
            <Pressable onPress={() => search.onChangeText('')} accessibilityRole="button" accessibilityLabel="Clear search"
              style={({ hovered }) => ({ width: 30, height: 30, borderRadius: 8, alignItems: 'center', justifyContent: 'center', backgroundColor: hovered ? palette.hover : 'transparent' })}>
              <Icon name="xmark" size={14} color={palette.icon} />
            </Pressable>
          ) : !isXS && !!countLabel ? <Text size="xs" color="secondary" style={{ fontVariant: ['tabular-nums'] }}>{countLabel}</Text> : null}
        </View>
      )}
      {tiles.length > 1 && (isXS ? (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginTop: 12, marginHorizontal: -padX }}
          contentContainerStyle={{ flexDirection: 'row', gap: 6, paddingHorizontal: padX }}>{tiles}</ScrollView>
      ) : <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>{tiles}</View>)}
    </View>
  );
}

export default React.memo(ToolsHero);
